import React from 'react';
import { Shield, Eye, Lock, Share2, UserCheck, Cookie, Terminal } from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdate = "24 de Março de 2026";

  return (
    <LegalLayout
      title="Política de Privacidade"
      description="Sua privacidade é nossa prioridade. Saiba como o Gastroflow protege e trata seus dados e os dados de seus clientes."
    >
      <div className="space-y-12 text-secondary/90 leading-relaxed">
        
        {/* Intro */}
        <section className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4 text-primary">
            <Shield className="w-8 h-8" />
            <h2 className="text-2xl font-black text-white">Compromisso Gastroflow</h2>
          </div>
          <p>
            O <strong>Gastroflow</strong> (referido como "nós", "plataforma" ou "Garçom Digital") valoriza a confiança depositada por estabelecimentos e seus clientes finais. Esta política detalha nossa conformidade com a <strong>LGPD (Lei Geral de Proteção de Dados - Lei nº 13.709/2018)</strong> e como garantimos a segurança da sua operação.
          </p>
          <p className="mt-4 text-sm text-secondary/50 font-medium italic">
            Última atualização: {lastUpdate}
          </p>
        </section>

        {/* 1. Dados Coletados */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Eye className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">1. Quais dados coletamos?</h2>
          </div>
          <p>Para garantir a fluidez da sua cozinha e salão, coletamos:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Do Estabelecimento:</strong> Razão social, CNPJ, e-mail do administrador, telefone, endereço e credenciais de integração (como Mercado Pago ou WhatsApp).</li>
            <li><strong>Dos Clientes do Restaurante:</strong> Nome, telefone e, opcionalmente, endereço para delivery e e-mail.</li>
            <li><strong>Dados de Transação:</strong> Histórico de pedidos, horários de reserva, ticket médio e preferências de consumo.</li>
            <li><strong>Dados Técnicos:</strong> Endereço IP, tipo de navegador, logs de sistema e identificadores de dispositivos para segurança.</li>
          </ul>
        </section>

        {/* 2. Finalidade */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Terminal className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">2. Para que finalidade usamos os dados?</h2>
          </div>
          <p>Utilizamos os dados estritamente para:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Operação do Negócio:</strong> Gestão de cardápio, processamento de pedidos em tempo real e controle de mesas.</li>
            <li><strong>Comunicação Automatizada:</strong> Envio de confirmações de reserva e status de pedidos via WhatsApp.</li>
            <li><strong>Processamento de Pagamento:</strong> Integração segura com gateways (Mercado Pago/Stripe).</li>
            <li><strong>Melhoria do Serviço:</strong> Análise anônima de métricas para otimizar a experiência do usuário.</li>
          </ul>
        </section>

        {/* 3. Compartilhamento */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Share2 className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">3. Com quem compartilhamos?</h2>
          </div>
          <p>
            O Gastroflow <strong>não vende dados</strong>. O compartilhamento ocorre apenas com parceiros essenciais (Operadores), sob contratos rigorosos de confidencialidade:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Infraestrutura:</strong> Google Cloud Platform e Cloudinary (armazenamento e processamento).</li>
            <li><strong>Mensageria:</strong> Meta/WhatsApp Business API para notificações.</li>
            <li><strong>Pagamentos:</strong> Mercado Pago para liquidação de transações.</li>
            <li><strong>Obrigação Legal:</strong> Autoridades judiciais quando formalmente solicitado.</li>
          </ul>
        </section>

        {/* 4. Segurança */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Lock className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">4. Segurança de Grau Bancário</h2>
          </div>
          <p>
            Implementamos protocolos rigorosos para proteger sua operação:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Criptografia de dados em trânsito (SSL/TLS).</li>
            <li>Arquitetura Multi-tenant (isolamento total entre restaurantes).</li>
            <li>Proteção contra ataques DDoS e monitoramento 24/7.</li>
            <li>Mascaramento de chaves de API sensíveis no backend.</li>
          </ul>
        </section>

        {/* 5. Direitos e Cookies */}
        <div className="grid md:grid-cols-2 gap-8">
          <section className="space-y-4 p-6 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-center gap-3 text-white">
              <UserCheck className="w-6 h-6 text-primary" />
              <h2 className="text-lg font-bold">Seus Direitos (LGPD)</h2>
            </div>
            <p className="text-sm">Você tem direito ao acesso, correção, anonimização ou exclusão de seus dados. Para solicitações, entre em contato via e-mail corporativo.</p>
          </section>

          <section className="space-y-4 p-6 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-center gap-3 text-white">
              <Cookie className="w-6 h-6 text-primary" />
              <h2 className="text-lg font-bold">Política de Cookies</h2>
            </div>
            <p className="text-sm">Usamos cookies essenciais para manter você logado e salvar preferências de exibição do painel administrativo.</p>
          </section>
        </div>

        <section className="pt-8 border-t border-white/10 text-center">
          <p className="text-sm text-secondary/60">
            Dúvidas sobre sua privacidade? <br />
            <a href="mailto:privacidade@garcomdigital.site" className="text-primary font-bold hover:underline">privacidade@garcomdigital.site</a>
          </p>
        </section>
      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicyPage;
